// ============================================================================
// PokerIntelCard - the player's own poker intel, on their own profile page
// ============================================================================
// The stats have existed server-side per hand (VPIP, PFR, showdown numbers,
// net bb, split by cash/tournament) and were only visible by tapping a seat
// at a table - a player could never see their OWN card. This surfaces the
// same /playerProfile data on the profile page with the same format toggle.
// ============================================================================
import React, { useEffect, useState } from 'react';
import {
  Box, HStack, Text, Spinner, useDisclosure, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalCloseButton, VStack,
} from '@chakra-ui/react';
import { Info } from 'lucide-react';
import gameApiService from '../../services/gameApiService';

// Plain-language definitions - most players have never seen tracker stats.
const STAT_HELP: Array<{ term: string; name: string; desc: string }> = [
  {
    term: 'HANDS', name: 'Hands played',
    desc: 'How many hands you have been dealt into. Every stat below is calculated from these hands, so the more you play, the more accurate they get.',
  },
  {
    term: 'VPIP', name: 'Voluntarily put chips in pot',
    desc: 'The percent of hands where you chose to call or raise before the flop. Posting the blinds does not count because that is forced. Around 20 to 30 percent is typical; much higher means playing a lot of hands, much lower is a very tight style.',
  },
  {
    term: 'PFR', name: 'Preflop raise',
    desc: 'The percent of hands where you raised before the flop. Compare it to VPIP: the closer the two numbers, the more aggressive the player, because they raise the hands they play instead of just calling.',
  },
  {
    term: 'WTSD', name: 'Went to showdown',
    desc: 'How often your hands go all the way to showdown, where the remaining players reveal their cards. A high number means you rarely let a hand go once you are in it.',
  },
  {
    term: 'W$SD', name: 'Won money at showdown',
    desc: 'Of the showdowns you reached, the percent where you won chips. Above 50 percent means the hands you take to showdown tend to be best.',
  },
  {
    term: 'NET BB', name: 'Net big blinds',
    desc: 'Your total profit or loss measured in big blinds instead of coins, so results from different stakes compare fairly. Positive means you are up overall; negative means down.',
  },
  {
    term: 'TOURNEYS', name: 'Tournament career',
    desc: 'Tournaments entered, how often you finished in the money (cash rate), tournaments won, and final tables reached.',
  },
];

type Fmt = 'all' | 'cash' | 'tourney';

interface Props { userId: string; }

const PokerIntelCard: React.FC<Props> = ({ userId }) => {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [fmt, setFmt] = useState<Fmt>('all');
  const helpDisclosure = useDisclosure();

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    gameApiService.playerProfile(userId).then(r => {
      if (!cancelled) { setData(r.success ? r : null); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [userId]);

  const gs = fmt === 'all' ? data?.gameStats
    : fmt === 'tourney' ? data?.tourneyStats
      : data?.cashStats;
  const career = data?.career;
  const pct = (v: number | null | undefined) => (v == null ? '—' : `${v}%`);

  // The per-format counters only started accruing when the split shipped;
  // older hands live in the combined totals. Say so instead of showing a
  // card full of dashes that reads like lost history.
  const splitIsYoung = fmt !== 'all'
    && (gs?.handsPlayed || 0) === 0
    && (data?.gameStats?.handsPlayed || 0) > 0;

  const cell = (value: React.ReactNode, label: string) => (
    <Box flex="1" textAlign="center" py={2}>
      <Text color="white" fontSize="lg" fontWeight="800">{value}</Text>
      <Text color="gray.500" fontSize="10px" letterSpacing="0.05em">{label}</Text>
    </Box>
  );

  const pill = (key: Fmt, label: string) => (
    <Box
      as="button"
      key={key}
      px={3} py={1}
      borderRadius="full"
      fontSize="11px"
      fontWeight="700"
      bg={fmt === key ? 'rgba(0,217,217,0.18)' : 'transparent'}
      color={fmt === key ? 'cyan.300' : 'gray.500'}
      border="1px solid"
      borderColor={fmt === key ? 'cyan.700' : 'gray.700'}
      onClick={() => setFmt(key)}
    >
      {label}
    </Box>
  );

  return (
    <Box w="100%">
      <HStack justify="space-between" mb={3} px={1}>
        <HStack spacing={1.5}>
          <Text color="gray.500" fontSize="xs" fontWeight="semibold">Player stats</Text>
          <Box as="button" aria-label="What do these stats mean?"
            color="gray.500" _hover={{ color: 'cyan.300' }}
            onClick={helpDisclosure.onOpen} lineHeight={0}>
            <Info size={13} />
          </Box>
        </HStack>
        <HStack spacing={1.5}>
          {pill('all', 'All')}
          {pill('cash', 'Cash')}
          {pill('tourney', 'Tourney')}
        </HStack>
      </HStack>

      <Modal isOpen={helpDisclosure.isOpen} onClose={helpDisclosure.onClose} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#16181d" color="white" mx={4} borderRadius="xl">
          <ModalHeader fontSize="md">What your stats mean</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>
            <VStack align="stretch" spacing={4}>
              {STAT_HELP.map(s => (
                <Box key={s.term}>
                  <HStack spacing={2} mb={0.5}>
                    <Text fontSize="xs" fontWeight="800" color="cyan.300" letterSpacing="0.05em">{s.term}</Text>
                    <Text fontSize="xs" color="gray.400">{s.name}</Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.300" lineHeight="1.55">{s.desc}</Text>
                </Box>
              ))}
              <Text fontSize="xs" color="gray.500" pt={1}>
                Stats update automatically after every hand you play. The All, Cash, and
                Tourney tabs filter them by game type.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Box bg="#1C1C1E" borderRadius="xl" px={3} py={2}>
        {loading ? (
          <Box textAlign="center" py={6}><Spinner size="sm" color="cyan.400" /></Box>
        ) : !data ? (
          <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
            Could not load your stats. Pull to refresh.
          </Text>
        ) : (
          <>
            <HStack spacing={0}>
              {cell((gs?.handsPlayed ?? 0).toLocaleString(), 'HANDS')}
              {cell(pct(gs?.vpipPct), 'VPIP')}
              {cell(pct(gs?.pfrPct), 'PFR')}
            </HStack>
            <HStack spacing={0} borderTop="1px solid" borderColor="whiteAlpha.100">
              {cell(pct(gs?.wtsdPct), 'WTSD')}
              {cell(pct(gs?.wsdPct), 'W$SD')}
              {cell(gs?.netBB == null ? '—'
                : `${gs.netBB > 0 ? '+' : ''}${gs.netBB.toLocaleString()}`, 'NET BB')}
            </HStack>
            {splitIsYoung && (
              <Text color="gray.600" fontSize="10.5px" textAlign="center" pb={2}>
                Hands played before the cash/tournament split are counted under All.
              </Text>
            )}
            {career && career.played > 0 && (
              <HStack spacing={0} borderTop="1px solid" borderColor="whiteAlpha.100">
                {cell(career.played, 'TOURNEYS')}
                {cell(`${career.cashRatePct}%`, 'CASH RATE')}
                {cell(career.won, 'WON')}
                {cell(career.finalTables, 'FINAL TABLES')}
              </HStack>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PokerIntelCard;
